import React, { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom-v5-compat";
import { styled } from "@twilio-paste/core/styling-library";
import { Box } from "@twilio-paste/core/box";
import { Flex } from "@twilio-paste/core/flex";
import { Heading } from "@twilio-paste/core/heading";
import { Paragraph } from "@twilio-paste/core/paragraph";
import { ButtonGroup } from "@twilio-paste/core/button-group";
import { Button } from "@twilio-paste/core/button";
import { SupportIcon } from "@twilio-paste/icons/esm/SupportIcon";
import { VoiceCapableIcon } from "@twilio-paste/icons/esm/VoiceCapableIcon";
import { TourIcon } from "@twilio-paste/icons/esm/TourIcon";
import { UserIcon } from "@twilio-paste/icons/esm/UserIcon";
import { ProductMessagingIcon } from "@twilio-paste/icons/esm/ProductMessagingIcon";
import {
  IntelligentDiscoveryHomeBlock,
  NewChatFormValues,
  WiseOwlElementIds,
} from "@twiliointernal/e2-commons-shared-definitions";
import {
  hasCdeErrorCodeParams,
  intelligentAssistantEventHandlerMap,
  CustomerDeliverabilityEngineQueryParams,
  CDEExternalPromptButtons,
  IntelligentAssistantEvents,
} from "@twiliointernal/org-twilio-customer-deliverability-engine-utils";

import { constants, copy } from "../../definitions";
import ChatFreeFormQuestionPopover from "../ChatFreeFormQuestionSamples";

import conversationalUseCaseIllustration from "../assets/conversationalUseCase.svg";
import { useWiseOwlContext } from "../../../../../hooks";

interface MessageErrorsPresentHomeProps {
  block: IntelligentDiscoveryHomeBlock;
}

const MessageErrorsPresentHomeContainer = styled(Box)`
  [data-paste-element="MESSAGE_STATUS_HEADER"] {
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5rem;
    margin-bottom: 0.5rem;
  }

  [data-paste-element="MESSAGE_STATUS_COPY"] {
    line-height: 1.25rem;
    margin-bottom: 0;
  }

  [data-paste-element="VIEW_ISSUES_BY"] {
    color: #606b85;
    margin-bottom: 1rem;
  }

  [data-paste-element="BUTTON_GROUP_1"] {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    row-gap: 0.5rem;
    column-gap: 0.5rem;
    justify-content: flex-start;
    align-items: flex-start;
  }

  [data-paste-element="BUTTON_GROUP_1"] > button:not(:first-of-type) {
    margin-left: 0;
  }
`;

const MessageErrorsPresentHome: React.FC<MessageErrorsPresentHomeProps> = ({
  block,
}) => {
  const {
    handlers: { handleStartNewChat },
  } = useWiseOwlContext();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const containsExternalCdeParams = hasCdeErrorCodeParams(location.search);

  const {
    values: { lastNDays, failedMessages, undeliveredMessages, totalMessages },
  } = block;

  // totalMessage include failed, undelivered, successful messages
  const percentMessagesFailed = `${Math.round(
    ((failedMessages + undeliveredMessages) / totalMessages) * 100
  )}%`;

  const handleOnStartNewChat = (data: NewChatFormValues): void => {
    if (handleStartNewChat) {
      handleStartNewChat(data);
    }
  };

  // on first render, if the URL contains CDE error code params, start a new chat
  useEffect(() => {
    if (containsExternalCdeParams && handleStartNewChat) {
      const searchParams = new URLSearchParams(
        decodeURIComponent(location.search)
      );
      const cdeErrorCode = searchParams.get(
        CustomerDeliverabilityEngineQueryParams.CDE_ERROR_CODE
      );
      const cdeErrorCodeName = searchParams.get(
        CustomerDeliverabilityEngineQueryParams.CDE_ERROR_CODE_NAME
      );
      handleStartNewChat({
        input: `Help me troubleshoot the error ${cdeErrorCode} - ${cdeErrorCodeName}`,
      });

      searchParams.delete(
        CustomerDeliverabilityEngineQueryParams.CDE_ERROR_CODE
      );
      searchParams.delete(
        CustomerDeliverabilityEngineQueryParams.CDE_ERROR_CODE_NAME
      );

      setSearchParams(searchParams);
    }
  }, [
    handleStartNewChat,
    containsExternalCdeParams,
    setSearchParams,
    location.search,
  ]);

  return (
    <MessageErrorsPresentHomeContainer
      element={WiseOwlElementIds.WISE_OWL_IDA_HOME_ERROR}
      marginTop="space30"
    >
      {/* logo and message error copy */}
      <Box paddingBottom="space10">
        <Flex hAlignContent="center" marginBottom="space50">
          <Box
            as="img"
            src={conversationalUseCaseIllustration}
            alt="conversational use case"
          />
        </Flex>
        <Heading
          as="h4"
          variant="heading40"
          element={constants.MESSAGE_STATUS_HEADER}
        >
          {copy.messageErrorsPresent.headers.messageStatusHeader(lastNDays)}
        </Heading>
        <Paragraph element={constants.MESSAGE_STATUS_COPY}>
          {copy.messageErrorsPresent.messageStatusCopy(
            failedMessages,
            undeliveredMessages,
            percentMessagesFailed
          )}
        </Paragraph>
      </Box>
      {/* preformed questions pill */}
      <Box
        paddingLeft="space10"
        paddingRight="space10"
        marginBottom="space40"
        data-testid="intelligent-assistant-prechat-error-state-pill-container"
      >
        <Heading as="h6" variant="heading60" element={constants.VIEW_ISSUES_BY}>
          {copy.messageErrorsPresent.headers.viewIssuesBy}
        </Heading>
        <ButtonGroup attached={false} element={constants.BUTTON_GROUP_1}>
          <Button
            variant="secondary"
            size="rounded_small"
            onClick={() =>
              handleOnStartNewChat({
                input: constants.prompts.topErrorCodes,
              })
            }
            data-testid="intelligent-assistant-prechat-error-state-error-codes-btn"
          >
            <SupportIcon decorative={true} />
            Error codes
          </Button>
          <Button
            element={constants.PHONE_NUMBERS_BTN}
            variant="secondary"
            size="rounded_small"
            data-testid="intelligent-assistant-prechat-error-state-phone-numbers-btn"
            onClick={() =>
              handleOnStartNewChat({
                input: constants.prompts.topPhoneNumbers,
              })
            }
          >
            <VoiceCapableIcon decorative={true} />
            Phone numbers
          </Button>
          <Button
            variant="secondary"
            size="rounded_small"
            data-testid="intelligent-assistant-prechat-error-state-countries-btn"
            onClick={() =>
              handleOnStartNewChat({
                input: constants.prompts.topCountries,
              })
            }
          >
            <TourIcon decorative={true} />
            Countries
          </Button>
          <Button
            variant="secondary"
            element={constants.SUB_ACCOUNTS_BTN}
            size="rounded_small"
            data-testid="intelligent-assistant-prechat-error-state-subaccounts-btn"
            onClick={() =>
              handleOnStartNewChat({
                input: constants.prompts.topSubaccounts,
              })
            }
          >
            <UserIcon decorative={true} />
            Subaccounts
          </Button>
          <Button
            variant="secondary"
            size="rounded_small"
            data-testid="intelligent-assistant-prechat-error-state-message-btn"
            onClick={() =>
              handleOnStartNewChat({
                input: constants.prompts.specificMessage,
              })
            }
          >
            <ProductMessagingIcon decorative={true} />
            {copy.messageErrorsPresent.callToAction.troubleshoot}
          </Button>
        </ButtonGroup>
      </Box>
      <ChatFreeFormQuestionPopover />
    </MessageErrorsPresentHomeContainer>
  );
};

export default MessageErrorsPresentHome;
